/* eslint-disable no-magic-numbers */
import React, { FC, useEffect, useState } from 'react';
import { IntlProvider, IntlConfig } from 'react-intl';

import { Logger } from '@futrli/shared-services';

import { loadPolyfills } from '../core/polyfills';
import { localeMap } from '../core';
import { setLocale as setIntlLocale } from '../actions';
import { addLocaleChangeEventListener, getLocalStorageLocale } from '../utils/locale';

interface Props extends Partial<IntlConfig> {
  children: React.ReactNode;
}

/**
 * Wraps the IntlProvider and injects our locale data.
 */
export const LocalisationProvider: FC<Props> = ({ textComponent = 'span', ...props }: Props) => {
  const [locale, setLocale] = useState(getLocalStorageLocale());
  const [messages, setMessages] = useState(localeMap[getLocalStorageLocale()]);

  /**
   * Add locale to dependencies & loadPolyfills function when we
   * support more than one language.
   * @see https://formatjs.io/docs/polyfills
   */
  useEffect(() => {
    loadPolyfills();
  }, []);

  // Whenever the locale state changes, update react-intl-redux
  useEffect(() => {
    setIntlLocale(locale);
  }, [locale]);

  // Listen out for 'localeChange' custom events and update the locale accordingly
  useEffect(() => {
    return addLocaleChangeEventListener(newLocale => {
      setLocale(newLocale);
      setMessages(localeMap[newLocale]);
    });
  }, []);

  const onError = (error: { code: string }) => {
    if (error?.code === 'MISSING_TRANSLATION') {
      // Suppress MISSING_TRANSLATION errors
      return;
    }
    // Log other errors
    Logger.error(JSON.stringify(error));
  };

  return (
    <IntlProvider locale={locale} textComponent={textComponent} messages={messages} onError={onError} {...props} />
  );
};
